/**
 * Wrap-selection input handler.
 *
 * When the user has a non-empty selection and types one of the configured
 * "wrapping" characters (e.g. `"`, `'`, `(`, `[`, `{`, `` ` ``), wrap each
 * selection range with the matching pair instead of replacing the selection.
 *
 * Hooks `beforeinput` directly (with highest precedence) so it runs before
 * any other extension's input handling.
 */

import { EditorSelection, Prec, type Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';

const PAIRS: Record<string, string> = {
  '"': '"',
  "'": "'",
  '`': '`',
  '(': ')',
  '[': ']',
  '{': '}',
};

export const wrapSelectionInputHandler = (): Extension =>
  Prec.highest(
    EditorView.domEventHandlers({
      beforeinput(event, view) {
        // Only intercept single-character text insertions.
        if (event.inputType !== 'insertText') return false;
        const text = event.data;
        if (!text || text.length !== 1) return false;

        const close = PAIRS[text];
        if (!close) return false;

        const { state } = view;
        // Only wrap when every selection range has content. If any cursor
        // is empty, fall through so closeBrackets handles auto-close
        // consistently across all cursors.
        if (state.selection.ranges.some((r) => r.empty)) return false;

        event.preventDefault();

        const changes = state.changeByRange((range) => {
          const slice = state.sliceDoc(range.from, range.to);
          return {
            changes: { from: range.from, to: range.to, insert: text + slice + close },
            range: EditorSelection.range(
              range.from + text.length,
              range.from + text.length + slice.length,
            ),
          };
        });

        view.dispatch(state.update(changes, { userEvent: 'input.type', scrollIntoView: true }));
        return true;
      },
    }),
  );
